import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import Header from './components/Header';
import SignIn from './components/SignIn';
import ProfileSetup from './components/ProfileSetup';
import UserProfile from './components/UserProfile';
import Feed from './components/Feed';
import Projects from './components/Projects';
import Settings from './components/Settings';
import ToastContainer from './components/ui/ToastContainer';
import PendingPostPage from './components/PendingPostPage';
import SearchPage from './components/SearchPage';
import './App.css';

// Custom component to handle @ routes since React Router doesn't handle /@:param well
const HandleRoute: React.FC = () => {
  const location = useLocation();
  const pathname = location.pathname;
  
  console.log('HandleRoute checking pathname:', pathname);
  
  // Check if URL starts with /@
  if (pathname.startsWith('/@')) {
    const pathWithoutSlash = pathname.substring(2); // Remove the /@
    console.log('Path without /@:', pathWithoutSlash);
      // Check if it's a post URL: /@handle/post/postId
    const postMatch = pathWithoutSlash.match(/^([^/]+)\/post\/([a-f0-9]{24})$/);
    if (postMatch) {
      const [, handle, postId] = postMatch;
      console.log('Post URL detected - handle:', handle, 'postId:', postId);
      
      // For post URLs, render Feed component directly (no redirect)
      // The Feed component will detect this URL pattern and open the appropriate post
      return (
        <PublicViewRoute>
          <Feed />
        </PublicViewRoute>
      );
    }
      // Check if it's a simple profile URL: /@handle
    if (pathWithoutSlash && pathWithoutSlash.length > 0 && !pathWithoutSlash.includes('/')) {
      console.log('Profile URL detected - handle:', pathWithoutSlash);
      
      // Valid @ profile route, render UserProfile with PublicViewRoute
      return (
        <PublicViewRoute>
          <UserProfile />
        </PublicViewRoute>
      );
    }
  }
  
  // If not a valid @ route, redirect to feed
  console.log('Invalid @ route, redirecting to feed');
  return <Navigate to="/feed" replace />;
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/signin" />;
  }

  // Redirect new users to profile setup
  if (!user.isProfileCompleted) {
    return <Navigate to="/setup" />;
  }

  return <>{children}</>;
};

// New route type that allows public viewing with optional authentication
const PublicViewRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  // If user exists but hasn't completed profile, redirect to setup
  if (user && !user.isProfileCompleted) {
    return <Navigate to="/setup" />;
  }

  // Allow both authenticated and non-authenticated users to view
  return <>{children}</>;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }
  if (user) {
    // If user exists but hasn't completed profile, redirect to setup
    if (!user.isProfileCompleted) {
      return <Navigate to="/setup" />;
    }
    // Otherwise redirect to user's profile
    return <Navigate to={`/@${user.handle}`} />;
  }

  return <>{children}</>;
};

const SetupRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/signin" />;
  }

  // If profile is already completed, redirect to feed
  if (user.isProfileCompleted) {
    return <Navigate to="/feed" />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <Router>
          <div className="App min-h-screen bg-gradient-to-br from-slate-900 via-custom-blue-800 to-slate-900">
            <Header />            <Routes>
              <Route path="/" element={<Navigate to="/feed" />} />
              <Route 
                path="/signin" 
                element={
                  <PublicRoute>
                    <SignIn />
                  </PublicRoute>
                } 
              />              <Route 
                path="/setup" 
                element={
                  <SetupRoute>
                    <ProfileSetup />
                  </SetupRoute>
                } 
              />
              <Route 
                path="/feed" 
                element={
                  <PublicViewRoute>
                    <Feed />
                  </PublicViewRoute>
                } 
              />              <Route 
                path="/projects" 
                element={
                  <ProtectedRoute>
                    <Projects />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/settings" 
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/pending-posts" 
                element={
                  <ProtectedRoute>
                    <PendingPostPage />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/search" 
                element={
                  <PublicViewRoute>
                    <SearchPage />
                  </PublicViewRoute>
                }
              />
              {/* Catch-all route - handle @ routes and other unmatched routes */}
              <Route 
                path="/*" 
                element={<HandleRoute />} 
              />
            </Routes>
          <ToastContainer />
        </div>
      </Router>
    </AuthProvider>
    </ToastProvider>
  );
}

export default App;
